/* eslint-disable react/jsx-curly-brace-presence */
import { forwardRef, useEffect, useRef, useCallback } from 'react';
import type { ForwardedRef, ElementRef } from 'react';
// import type { PrimitivePropsWithRef } from '@radix-ui/react-primitive';
// import type { DialogProps } from '@radix-ui/react-dialog';
// import { Primitive } from '@radix-ui/react-primitive';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useStore } from '@tmsw/powerups.store';
import { useVieConfig } from '@tmsw/powerups.vie.hooks.use-vie-config';
import {
  CircleButton,
  CircleButtonProps,
} from '@tmsw/powerups.ui.buttons.circle-button';
import { useTranslation } from '@tmsw/powerups.intl.hooks.use-translation';
import { useSettingsConfig } from './useSettingsConfig';
import { ListItemVariantProps } from './OptionsMenuListItem';
import './ui-dialogs-settings.css';

// interface PrimitiveForwardRefComponent<E extends React.ElementType>
//   extends React.ForwardRefExoticComponent<PrimitivePropsWithRef<E>> {}
// export type LayerElement = ElementRef<typeof Primitive.div | PrimitiveForwardRefComponent<'section'>>;
export type LayerOnOpenChangeHandler = (open: boolean) => void;
// export interface LayerOptions extends DialogProps {
//   onOpenChange: LayerOnOpenChangeHandler;
//   open: boolean;
//   modal?: boolean;
// }

// export interface LayerEvents {
//   onAfterMount?: () => void;
// }

// export interface LayerProps extends LayerOptions {
//   element: LayerElement;
//   eventHandlers?: LayerEvents;
// }

// const useLayerContext = () => {
//   return {
//       /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
//       onUnMountLayer: (layerName: string) => {},
//       /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
//       onMountLayer: (layerName: string, layerProps: LayerProps) => () => {}
//   }
// };

// dialog buttons are secretly circle buttons
export type DialogButtonProps = CircleButtonProps;

const buttonDefaults = {
  icon: 'dots',
  size: 'md',
  css: {
    height: 'var(--ui-buttons-circle-button-height)',
    width: 'var(--ui-buttons-circle-button-width)',
  },
  className: 'ui-dialogs-settings ui-buttons-circle-button',
};

export function SettingsDialog() {
  const parentNode = 'SETTINGS_PANEL';
  const { __t } = useTranslation(parentNode);

  const settingsTogglerRef = useRef<HTMLButtonElement>(null);
  // const closeTogglerRef = useRef<HTMLButtonElement>(null);
  // const backTogglerRef = useRef<HTMLButtonElement>(null);
  const panelTogglerRef = useRef<HTMLButtonElement>(null);
  const buttonRefMap = {
    trigger: settingsTogglerRef,
    panel: panelTogglerRef,
    // back: backTogglerRef,
  };

  const pageTitle = useStore((state) => state.pageTitle);
  const setPageTitle = useStore((state) => state.setPageTitle);

  // const isPauseEnabled = useStore((state) => state.isPauseEnabled);
  const play = useStore((state) => state.play);
  const setPlay = useStore((state) => state.setPlay);
  const currentPage = useStore((state) => state.currentPage);
  const open = useStore((state) => state.showSettingsPanel);
  const setOpen = useStore((state) => state.setShowSettingsPanel);
  const currentPanel =
    useStore((state) => state.currentSettingsMenuPanel) || 'OptionsMenu';
  const setCurrentPanel = useStore(
    (state) => state.setCurrentSettingsMenuPanel
  );

  const originalPageTitleRef = useRef(pageTitle);

  const { goToPage } = useVieConfig(null);

  // const togglerHasFocus = (type = 'open') => {
  //   const ref = buttonRefMap[type];
  //   console.log('focusToggler', type, ref);
  //   if (!ref.current) {
  //     console.trace('focusToggler not found', type);
  //   }
  //   return ref.current.id !== document.activeElement?.id;
  // };

  const focusToggler = (type = 'trigger') => {
    const ref = buttonRefMap[type];
    console.log('focusToggler', type, ref, document.activeElement);
    if (!ref.current) {
      console.trace('focusToggler not found', type);
    }
    if (ref.current.id !== document.activeElement?.id) {
      ref.current.focus();
      console.log(document.activeElement);
    }
  };

  const onOpenChange: LayerOnOpenChangeHandler = useCallback((nextOpen) => {
    setOpen(nextOpen);
    // revert to original pageTitle onClose
    if (!nextOpen) {
      setPageTitle(originalPageTitleRef.current);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const toggleDialog = () => onOpenChange(!open);
  // const openDialog = () => onOpenChange(true);
  const closeDialog = () => onOpenChange(false);

  // share with everyone that needs it
  const options = {
    play,
    setPlay,
    goToPage,
    currentPanel,
    setCurrentPanel,
    toggleDialog,
    closeDialog,
    __t,
  };

  // useEffect(() => {
  //   /**
  //    * @todo Restore the previous video player state instead of forcing play
  //    * @returns {void}
  //    */
  //   const setPlayability = () => {
  //     if (isPauseEnabled) return; // if video is already paused before settings panel opened, then avoid calling setPlay()
  //     if (open && play === true) {
  //       // when Settings Panel is open
  //       setPlay(false);
  //     } else if (play === false) {
  //       setPlay(true);
  //     }
  //   };
  //   setPlayability();
  //   /* eslint-disable-next-line react-hooks/exhaustive-deps */
  // }, [open, isPauseEnabled]);

  const settingsConfig = useRef(useSettingsConfig(options)).current;
  const isRoot = settingsConfig.root.name === currentPanel;
  const item = isRoot
    ? settingsConfig.root
    : (settingsConfig.root.items.find(
        (p) => p.name === currentPanel && p.variant === 'panel'
      ) as ListItemVariantProps<'panel'>);
  const PanelComponent = item.component;

  // OptionsMenu gets the entire config; submenus and list items get what they need
  const panelProps = isRoot ? settingsConfig.root : { ...item, options };

  const buttonConfig = {
    open: {
      ...buttonDefaults,
      icon: 'dots',
      ariaLabel: __t('openSettingsPanel'),
      id: 'SettingsPanel-Toggler',
    },
    close: {
      ...buttonDefaults,
      icon: 'close',
      ariaLabel:
        __t('closeSettingsPanel') || __t('closeButton.ariaLabel', 'GENERAL'),
      onClick: () => toggleDialog(),
      id: 'SettingsPanel-Close',
      'aria-hidden': !isRoot,
      // css: !isRoot ? { visibility: 'hidden' } : {},
      // autoFocus: open && isRoot,
      autoFocus: true,
    },
    back: {
      ...buttonDefaults,
      icon: 'back',
      ariaLabel:
        __t('backToSettingsPanel') || __t('backButton.ariaLabel', 'GENERAL'),
      onClick: () => setCurrentPanel('OptionsMenu'),
      id: 'SettingsPanel-Back',
      'aria-hidden': isRoot,
      // css: isRoot ? { visibility: 'hidden' } : {},
      // autoFocus: open && !isRoot,
      autoFocus: true,
    },
  };

  const DialogButton = forwardRef(
    (props: DialogButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
      <CircleButton ref={ref} {...props} />
    )
  );
  const DialogButtonBack = forwardRef(
    (props: DialogButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
      <DialogButton ref={ref} {...props} {...buttonConfig.back} />
    )
  );
  const DialogButtonClose = forwardRef(
    (props: DialogButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
      <DialogButton ref={ref} {...props} {...buttonConfig.close} />
    )
  );

  // focus settings toggler on mount
  // useEffect(() => {
  //   focusToggler('open');
  // }, []);

  // useEffect(() => {
  //   focusToggler('open');
  // }, [currentPage]);

  // useEffect(() => {
  //   if (!isRoot && open) {
  //     focusToggler('back');
  //   }
  // }, [currentPanel, isRoot, open]);

  // useEffect(() => {
  //   // setTimeout(() => {
  //     const openButton = document.getElementById('SettingsPanel-Toggler');
  //     const closeButton = document.getElementById('SettingsPanel-Close');
  //     const backButton = document.getElementById('SettingsPanel-Back');
  //     // console.log('openButton, closeButton', openButton, closeButton);
  //     if(open && closeButton) {
  //       // console.log('Focusing on close button');
  //       document.activeElement.hasAttribute('blur') && .blur();
  //       closeButton.focus();
  //     }
  //     else if(openButton) {
  //       // console.log('Focusing on open button');
  //       openButton.focus();
  //     } else if (backButton) {
  //       // console.log('Focusing on back button');
  //       backButton.focus();
  //     }
  //   // }, 50);
  // }, [open, isRoot]);

  /* eslint-disable-next-line react/prop-types */
  const PanelComponentWrapper = ({ children }) => {
    return (
      <div className="SettingsPanel" data-panel={currentPanel}>
        {isRoot ? (
          <Dialog.Close asChild>
            <DialogButtonClose ref={panelTogglerRef} />
          </Dialog.Close>
        ) : (
          <DialogButtonBack ref={panelTogglerRef} />
        )}
        {children}
      </div>
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {
        <Dialog.Trigger asChild>
          <DialogButton
            ref={settingsTogglerRef}
            aria-hidden={open}
            {...buttonConfig.open}
          />
        </Dialog.Trigger>
      }
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          // onOpenAutoFocus={(e) => {
          //   e.preventDefault();
          //   focusToggler('close');
          // }}
          // onCloseAutoFocus={(e) => {
          //   e.preventDefault();
          //   focusToggler('open');
          // }}
        >
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{item.heading}</Dialog.Title>
          </VisuallyHidden.Root>
          <PanelComponentWrapper>
            <PanelComponent {...panelProps} />
          </PanelComponentWrapper>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
